import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFirestore, doc, getDoc } from "firebase/firestore"; // Keep for user data fetching
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheck, // Use simple check for features
    faCogs, // AI-Powered Efficiency
    faChartLine, // Deeper Insights
    faBalanceScale, // Fairness Focused
    faTasks, // Seamless Experience
    faShieldAlt, // GDPR Compliant
    faStar, // Most Popular icon
    faBuilding, // Enterprise Icon/Placeholder
    faQuestionCircle, // FAQ Icon
    // Add specific feature icons if needed, using faCheck is cleaner for lists
    faUsers, // Candidate Management
    faEnvelope, // Email Notifications
    faDatabase, // Data Access
    faInfinity, // Unlimited Apps/Support
    faRobot, // AI CV Analysis
    faBrain, // Psychoanalytic
    faCode, // Technical Assessments
    faPlusCircle, // Build Assessments
    faBan, // Bias Elimination
    faSearchLocation, // Sourcing
    faPalette, // Branded Page/Notifications
    faNetworkWired, // API Access
    faUserTie, // Dedicated Manager
    faLifeRing, // Priority Support
} from "@fortawesome/free-solid-svg-icons";
import TawkToChat from "../components/TawkToChat";
import { useAuth } from "../useAuth";

// --- Data ---
// Define base prices in USD, conversion factors/prices for others can be added here
// Storing like this is simpler for display than complex Firestore structure per currency per tier
const pricingTiersData = [
    {
        id: "basic",
        nameKey: "tier.basic.name",
        prices: { USD: 20, EUR: 18, XOF: 12000, CAD: 27 }, // Add other currencies as needed
        targetAudienceKey: "tier.basic.target",
        valuePropKey: "tier.basic.valueProp",
        features: [
            { key: "tier.basic.feature1", icon: faUsers }, // Candidate Management System
            { key: "tier.basic.feature2", icon: faEnvelope }, // Automated Email Notifications
            { key: "tier.basic.feature3", icon: faDatabase }, // Up to 1000 Applications per Job Posting
            { key: "tier.basic.feature4", icon: faDatabase }, // 18 Months Data Access
            { key: "tier.basic.feature5", icon: faInfinity }, // Unlimited Support (During Recruitment)
            { key: "tier.basic.feature6", icon: faShieldAlt }, // GDPR Compliant Platform
        ],
        ctaKey: "tier.basic.cta",
        link: "/signup", // Or specific trial link
        isPopular: false,
        isEnterprise: false,
    },
    {
        id: "growth",
        nameKey: "tier.growth.name",
        prices: { USD: 80, EUR: 75, XOF: 48000, CAD: 110 },
        targetAudienceKey: "tier.growth.target",
        valuePropKey: "tier.growth.valueProp",
        features: [
            { key: "tier.growth.feature1", icon: faCheck, bold: true }, // All Basic Features, PLUS:
            { key: "tier.growth.feature2", icon: faInfinity }, // Unlimited Applications per Job Posting
            { key: "tier.growth.feature3", icon: faRobot }, // Automatic CV Analysis & Ranking
            { key: "tier.growth.feature4", icon: faDatabase }, // 24+ Months Data Access (Ongoing)
        ],
        ctaKey: "tier.growth.cta",
        link: "/signup",
        isPopular: false,
        isEnterprise: false,
    },
    {
        id: "professional",
        nameKey: "tier.professional.name",
        prices: { USD: 160, EUR: 150, XOF: 96000, CAD: 220 },
        targetAudienceKey: "tier.professional.target",
        valuePropKey: "tier.professional.valueProp",
        features: [
            { key: "tier.professional.feature1", icon: faCheck, bold: true }, // All Growth Features, PLUS:
            { key: "tier.professional.feature2", icon: faBrain }, // Psychoanalytic Candidate Assessments
            { key: "tier.professional.feature3", icon: faCode }, // Technical Assessment Library
            { key: "tier.professional.feature4", icon: faPlusCircle }, // Build & Customize Your Own Assessments
            { key: "tier.professional.feature5", icon: faBan }, // Bias Elimination Tools
        ],
        ctaKey: "tier.professional.cta",
        link: "/signup",
        isPopular: true,
        isEnterprise: false,
    },
    {
        id: "premium",
        nameKey: "tier.premium.name",
        prices: { USD: 250, EUR: 230, XOF: 150000, CAD: 340 },
        targetAudienceKey: "tier.premium.target",
        valuePropKey: "tier.premium.valueProp",
        features: [
            { key: "tier.premium.feature1", icon: faCheck, bold: true }, // All Professional Features, PLUS:
            { key: "tier.premium.feature2", icon: faSearchLocation }, // Sourcing of Top Candidates
            { key: "tier.premium.feature3", icon: faPalette }, // Custom Branded Application Page
            { key: "tier.premium.feature4", icon: faPalette }, // Custom Branded Candidate Notifications
        ],
        ctaKey: "tier.premium.cta",
        link: "/signup",
        isPopular: false,
        isEnterprise: false,
    },
    {
        id: "enterprise",
        nameKey: "tier.enterprise.name",
        prices: { USD: 450 }, // Base price for indication, but text will say Custom
        targetAudienceKey: "tier.enterprise.target",
        valuePropKey: "tier.enterprise.valueProp",
        features: [
            { key: "tier.enterprise.feature1", icon: faCheck, bold: true }, // All Premium Features, PLUS:
            { key: "tier.enterprise.feature2", icon: faDatabase }, // Unlimited Data Access (Including Archives)
            { key: "tier.enterprise.feature3", icon: faUserTie }, // Dedicated Account Manager
            { key: "tier.enterprise.feature4", icon: faNetworkWired }, // Advanced Integrations / API Access
            { key: "tier.enterprise.feature5", icon: faLifeRing }, // Priority Support
        ],
        ctaKey: "tier.enterprise.cta",
        link: "/contact-sales", // Link to contact form/page
        isPopular: false,
        isEnterprise: true,
    },
];

const currencies = {
    XOF: { symbol: "FCFA", label: "CFA Franc BCEAO (XOF)" },
    USD: { symbol: "$", label: "US Dollar (USD)" },
    EUR: { symbol: "€", label: "Euro (EUR)" },
    CAD: { symbol: "$", label: "Canadian Dollar (CAD)" },
    // Add the rest of your extensive currency list here...
    AED: { symbol: "AED", label: "United Arab Emirates Dirham (AED)" },
    // ... (keep the full list from your original code)
    ZMW: { symbol: "ZK", label: "Zambian Kwacha (ZMW)" },
};

// --- Helper Functions ---
const getCurrencyFromCountryCode = (countryCode) => {
    const countryCurrencyMap = {
        US: "USD", GB: "GBP", JP: "JPY", // Add more mappings
        FR: "EUR", DE: "EUR", IT: "EUR", ES: "EUR", // European countries using EUR
        CA: "CAD", AU: "AUD", AE: "AED", // Others
        TG: "XOF", BJ: "XOF", BF: "XOF", CI: "XOF", GW: "XOF", ML: "XOF", NE: "XOF", SN: "XOF",
        // Add more country code to currency code mappings
    };
    const upperCaseCode = countryCode?.toUpperCase();
    return countryCurrencyMap[upperCaseCode] || "USD"; // Default to USD
};

const formatPrice = (priceValue, currencyCode, t) => {
    const currencyInfo = currencies[currencyCode];
    if (priceValue === null || priceValue === undefined) {
        return t('customQuote'); // Handle Enterprise case or missing prices
    }
    const options = {
        style: "decimal",
        minimumFractionDigits: currencyCode === 'XOF' ? 0 : 2, // No decimals for XOF
        maximumFractionDigits: currencyCode === 'XOF' ? 0 : 2,
    };
    // Basic number formatting, adjust locale if needed for specific comma/dot separators
    return priceValue.toLocaleString(currencyCode === 'XOF' ? 'fr-FR' : 'en-US', options);
};


// --- Sub-Components ---

const HeaderSection = ({ t }) => (
    <section className="text-center mb-12 md:mb-16">
        {/* Choose one headline */}
        {/* <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 font-bold tracking-tight mb-4">{t('header.headline1')}</h1> */}
        {/* <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 font-bold tracking-tight mb-4">{t('header.headline2')}</h1> */}
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 font-bold tracking-tight mb-4">{t('header.headline3')}</h1>

        <p className="mt-4 text-lg md:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto mb-8">
            {t('header.subheadline')}
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-4xl mx-auto text-center">
            <div className="flex flex-col items-center">
                <FontAwesomeIcon icon={faCogs} className="text-indigo-600 text-3xl mb-2" />
                <span className="text-sm font-medium text-slate-600 leading-relaxed">{t('header.point1')}</span>
            </div>
            <div className="flex flex-col items-center">
                <FontAwesomeIcon icon={faChartLine} className="text-indigo-600 text-3xl mb-2" />
                <span className="text-sm font-medium text-slate-600 leading-relaxed">{t('header.point2')}</span>
            </div>
            <div className="flex flex-col items-center">
                <FontAwesomeIcon icon={faBalanceScale} className="text-indigo-600 text-3xl mb-2" />
                <span className="text-sm font-medium text-slate-600 leading-relaxed">{t('header.point3')}</span>
            </div>
            <div className="flex flex-col items-center">
                <FontAwesomeIcon icon={faTasks} className="text-indigo-600 text-3xl mb-2" />
                <span className="text-sm font-medium text-slate-600 leading-relaxed">{t('header.point4')}</span>
            </div>
             <div className="flex flex-col items-center col-span-2 md:col-span-1">
                <FontAwesomeIcon icon={faShieldAlt} className="text-indigo-600 text-3xl mb-2" />
                <span className="text-sm font-medium text-slate-600 leading-relaxed">{t('header.point5')}</span>
            </div>
        </div>
    </section>
);

const CurrencySelector = ({ currency, handleCurrencyChange, currencies, t }) => (
    <section className="text-center mb-10 md:mb-12">
        <label htmlFor="currency" className="mr-2 font-medium text-slate-600 leading-relaxed text-lg">
            {t('selectCurrency')}:
        </label>
        <select
            id="currency"
            name="currency"
            value={currency}
            onChange={handleCurrencyChange}
            className="border border-gray-300 rounded-md px-4 py-2 text-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
            aria-label={t('selectCurrency')}
        >
            {Object.entries(currencies).map(([code, { label }]) => (
                <option key={code} value={code}>
                    {label}
                </option>
            ))}
        </select>
    </section>
);

const PricingTierCard = ({ tier, currency, t, currentUser }) => {
    const { id, nameKey, prices, targetAudienceKey, valuePropKey, features, ctaKey, link, isPopular, isEnterprise } = tier;
    const priceValue = isEnterprise ? null : prices[currency] ?? prices['USD']; // Fallback to USD if currency price missing
    const currencySymbol = currencies[currency]?.symbol || '$';
    const formattedPrice = formatPrice(priceValue, currency, t);
    const ctaText = t(ctaKey);
    const finalLink = currentUser && !isEnterprise ? `/checkout?plan=${id}&currency=${currency}` : (isEnterprise ? link : "/signup"); // Adjust checkout link

    return (
        <div className={`bg-white rounded-lg shadow-lg overflow-hidden flex flex-col relative border-2 ${isPopular ? 'border-indigo-600' : 'border-transparent'} hover:shadow-xl transition-shadow duration-300 ease-in-out`}>
            {isPopular && (
                <div className="absolute top-0 right-0 mt-2 mr-2 sm:mt-4 sm:mr-4 z-10">
                    <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-semibold bg-indigo-600 text-white shadow-md">
                        <FontAwesomeIcon icon={faStar} className="mr-1.5" /> {t('mostPopular')}
                    </span>
                </div>
            )}
            {/* Header */}
            <div className="px-6 py-8 bg-white sm:p-10 sm:pb-6 text-center">
                <h3 className="text-xl font-semibold tracking-wide text-indigo-700 uppercase mb-2">
                    {t(nameKey)}
                </h3>
                 <p className="text-sm text-slate-500 mb-4 min-h-[40px]">{t(targetAudienceKey)}</p> {/* Target Audience */}

                <div className="mt-4 flex justify-center items-baseline text-5xl font-extrabold text-slate-900 font-bold tracking-tight">
                   {!isEnterprise && (
                     <>
                        <span className="text-3xl font-medium mr-1">{currencySymbol}</span>
                        {formattedPrice}
                         <span className="text-xl font-medium text-slate-500 ml-1">/{t('perMonth')}</span>
                     </>
                   )}
                   {isEnterprise && (
                       <span className="text-3xl font-semibold">{formattedPrice}</span> // "Custom Quote"
                   )}

                </div>
                <p className="mt-3 text-base text-slate-600 leading-relaxed min-h-[60px]">{t(valuePropKey)}</p>
            </div>

            {/* Features */}
            <div className="flex-1 px-6 pt-6 pb-8 bg-gray-50 sm:p-10 sm:pt-6">
                <ul className="space-y-4">
                    {features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                            <FontAwesomeIcon icon={feature.icon || faCheck} className={`flex-shrink-0 w-5 h-5 mt-0.5 ${feature.bold ? 'text-indigo-600' : 'text-green-500'}`} />
                            <span className={`ml-3 text-slate-600 leading-relaxed ${feature.bold ? 'font-semibold' : ''}`}>{t(feature.key)}</span>
                        </li>
                    ))}
                </ul>
            </div>

             {/* CTA */}
            <div className="px-6 pb-8 bg-gray-50 sm:px-10">
                 <Link
                    to={finalLink}
                    className={`block w-full text-center px-6 py-3 border border-transparent rounded-md shadow text-base font-medium text-white ${
                        isPopular
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : isEnterprise
                        ? 'bg-gray-800 hover:bg-gray-900'
                        : 'bg-indigo-500 hover:bg-indigo-600'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                >
                    {ctaText}
                </Link>
            </div>
        </div>
    );
};

const PricingTiers = ({ tiersData, currency, t, currentUser }) => (
    <section className="mt-12">
        {/* Adjust grid columns for responsiveness and number of tiers */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 xl:grid-cols-5 lg:gap-y-10 xl:gap-x-6">
            {/* Basic and Growth on first row (example layout for 5 tiers) */}
            <div className="lg:col-span-1 xl:col-span-1"> <PricingTierCard tier={tiersData[0]} currency={currency} t={t} currentUser={currentUser}/> </div>
            <div className="lg:col-span-1 xl:col-span-1"> <PricingTierCard tier={tiersData[1]} currency={currency} t={t} currentUser={currentUser}/> </div>

            {/* Professional (Most Popular) takes center stage or full width on medium */}
            <div className="lg:col-span-1 xl:col-span-1"> <PricingTierCard tier={tiersData[2]} currency={currency} t={t} currentUser={currentUser}/> </div>

             {/* Premium and Enterprise on next row/area */}
            <div className="lg:col-span-1 xl:col-span-1"> <PricingTierCard tier={tiersData[3]} currency={currency} t={t} currentUser={currentUser}/> </div>
            <div className="lg:col-span-3 xl:col-span-1"> <PricingTierCard tier={tiersData[4]} currency={currency} t={t} currentUser={currentUser}/> </div>
        </div>
    </section>
);

// --- Optional Detailed Feature Comparison Table ---
const FeatureComparisonTable = ({ tiersData, currency, t }) => {
    // This is a placeholder - implementation requires defining all unique features
    // across tiers and mapping them in a table structure.
    // Consider using a dedicated table library or careful Tailwind styling.
    const features = [ /* Define all features here */ ];
    const tiers = tiersData;

     return (
        <section className="mt-16 hidden md:block"> {/* Hide on small screens */}
             <h2 className="text-3xl font-extrabold text-slate-900 font-bold tracking-tight text-center mb-8">{t('featureComparison.title')}</h2>
             <div className="overflow-x-auto bg-white shadow rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">{t('featureComparison.feature')}</th>
                             {tiers.map(tier => (
                                <th key={tier.id} scope="col" className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                                     {t(tier.nameKey)}
                                     <p className="text-lg font-bold text-slate-900 font-bold tracking-tight">
                                         {tier.isEnterprise ? t('customQuote') : `${currencies[currency]?.symbol || '$'}${formatPrice(tier.prices[currency] ?? tier.prices['USD'], currency, t)}`}
                                    </p>
                                 </th>
                             ))}
                         </tr>
                     </thead>
                     <tbody className="bg-white divide-y divide-gray-200">
                         {/* Example Row - Repeat for all features */}
                         <tr>
                             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 font-bold tracking-tight sticky left-0 bg-white z-10">{t('tier.basic.feature1')}</td>
                            <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                         </tr>
                         <tr>
                             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 font-bold tracking-tight sticky left-0 bg-white z-10">{t('tier.growth.feature3')}</td>
                            <td className="px-6 py-4 text-center text-gray-400">-</td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                             <td className="px-6 py-4 text-center"><FontAwesomeIcon icon={faCheck} className="text-green-500 mx-auto" /></td>
                         </tr>
                         {/* Add more feature rows here, checking availability per tier */}
                     </tbody>
                 </table>
             </div>
         </section>
     );
 };

// --- FAQ Section ---
const FAQSection = ({ t }) => {
    // Basic FAQ structure, could be enhanced with accordion interactivity
    const faqs = [
        { qKey: 'faq.q1', aKey: 'faq.a1' },
        { qKey: 'faq.q2', aKey: 'faq.a2' },
        { qKey: 'faq.q3', aKey: 'faq.a3' },
        { qKey: 'faq.q4', aKey: 'faq.a4' },
        { qKey: 'faq.q5', aKey: 'faq.a5' },
        { qKey: 'faq.q6', aKey: 'faq.a6' },
        { qKey: 'faq.q7', aKey: 'faq.a7' },
    ];

    return (
        <section className="mt-16 pt-10 border-t border-gray-200">
            <h2 className="text-3xl font-extrabold text-slate-900 font-bold tracking-tight text-center mb-8">{t('faq.title')}</h2>
            <div className="max-w-3xl mx-auto space-y-6">
                {faqs.map((faq, index) => (
                    <div key={index} className="bg-white p-5 rounded-lg shadow">
                        <h3 className="text-lg font-medium text-slate-900 font-bold tracking-tight mb-2 flex items-center">
                            <FontAwesomeIcon icon={faQuestionCircle} className="text-indigo-600 mr-2" /> {t(faq.qKey)}
                        </h3>
                        <p className="text-slate-600 leading-relaxed">{t(faq.aKey)}</p>
                    </div>
                ))}
            </div>
        </section>
    );
};

// --- Footer CTA ---
const FooterCTA = ({ t }) => (
    <section className="mt-16 py-12 bg-gray-800 text-center rounded-lg shadow-inner">
        <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-4">
            {t('footerCta.headline')}
        </h2>
        <p className="mt-4 text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
            {t('footerCta.text')}
        </p>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
                to="/signup" // Link to Basic or trial sign-up
                className="inline-flex justify-center items-center px-8 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-600 bg-white hover:bg-indigo-50"
            >
                {t('footerCta.button1')}
            </Link>
             <Link
                to="/contact-sales"
                className="inline-flex justify-center items-center px-8 py-3 border border-indigo-400 text-base font-medium rounded-md shadow-sm text-white bg-transparent hover:bg-indigo-700 hover:border-indigo-700"
            >
                {t('footerCta.button2')}
            </Link>
        </div>
    </section>
);


// --- Main Pricing Component ---
const Pricing = () => {
    const { t } = useTranslation("Pricing");
    const [currency, setCurrency] = useState("USD"); // Default currency
    const { currentUser } = useAuth();
    const db = getFirestore(); // Keep db if needed for user currency pref fetch

    // Effect to determine initial currency (User Pref > IP Fallback > Default)
    useEffect(() => {
        let isMounted = true; // Prevent state update on unmounted component

        const determineCurrency = async () => {
            let determinedCurrency = "USD"; // Start with default

            // 1. Check logged-in user preference
            if (currentUser) {
                try {
                    const userDocRef = doc(db, "users", currentUser.email); // Assuming email is user ID
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data().userData;
                        const userPrefCurrency = userData?.companyCountry?.currency?.toUpperCase();
                        if (userPrefCurrency && currencies[userPrefCurrency]) {
                            determinedCurrency = userPrefCurrency;
                        }
                    }
                } catch (error) {
                    console.warn("Error fetching user currency preference:", error);
                }
            }

            // 2. If no user pref, try IP detection (only if currency is still default)
            if (determinedCurrency === "USD" && !currentUser) { // Only run IP check if needed and no user logged in
                 try {
                     const ipResponse = await fetch("https://ipapi.co/json/");
                     if (ipResponse.ok) {
                        const ipData = await ipResponse.json();
                        const userCountryCode = ipData?.country_code; // Use country_code
                        if (userCountryCode) {
                             const currencyFromIP = getCurrencyFromCountryCode(userCountryCode);
                             if (currencies[currencyFromIP]) {
                                 determinedCurrency = currencyFromIP;
                            }
                        }
                     } else {
                         console.warn("IP API request failed. Status:", ipResponse.status);
                     }
                 } catch (ipError) {
                    console.warn("IP API request failed:", ipError);
                 }
            }

            // Set the determined currency if component is still mounted
            if (isMounted) {
                setCurrency(determinedCurrency);
            }
        };

        determineCurrency();

        return () => { isMounted = false; }; // Cleanup function

    }, [currentUser, db]); // Rerun on user change


    const handleCurrencyChange = (event) => {
        setCurrency(event.target.value);
    };

    // Removed loading/error states related to Firestore price fetching
    // Add them back if you absolutely need to fetch prices async

    return (
        <div className="bg-gray-100 py-12 md:py-20 font-sans">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

                <HeaderSection t={t} />

                <CurrencySelector
                    currency={currency}
                    handleCurrencyChange={handleCurrencyChange}
                    currencies={currencies}
                    t={t}
                />

                <PricingTiers
                    tiersData={pricingTiersData}
                    currency={currency}
                    t={t}
                    currentUser={currentUser}
                 />

                 {/* Optional: Feature Comparison Table */}
                 {/* <FeatureComparisonTable tiersData={pricingTiersData} currency={currency} t={t} /> */}


                 <FAQSection t={t} />

                 <FooterCTA t={t} />

            </div>
             <TawkToChat /> {/* Assuming you want chat on this page */}
        </div>
    );
};

export default Pricing;